// src/context/RoleContext.jsx
//
// Per-team role lookup. There is NO "user mode" — role is a property
// of each team relationship, not a global flag the user toggles. The
// app's home is the team picker (/dashboard); each team's detail
// page renders role-aware content based on the user's role on that
// SPECIFIC team. No sticky context, no auto-flip, no toggle.
//
// What this context provides:
//   • teamRole(teamId)       — 'owner' | 'staff' | 'parent' | null
//   • isCoachOnTeam(teamId)  — boolean shorthand (owner/staff)
//   • isParentOnTeam(teamId) — boolean shorthand
//   • parentTeamIds          — Set of team IDs the user is a parent on
//                              (used by Home to hydrate parent teams,
//                              since TeamContext only queries coach
//                              fields)
//   • hasParentTeams         — boolean shorthand
//   • hasCoachTeams          — boolean shorthand (does the user
//                              have any coach-side memberships?)
//   • isParent               — global signup-role hint, retained for
//                              the few places that need it (welcome
//                              modal copy, post-signup redirect).
//                              NOT used to drive UI rendering anymore.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from './AuthContext';
import { useTeam } from './TeamContext';

const RoleContext = createContext(null);

export function RoleProvider({ children }) {
  const { userProfile, firebaseUser } = useAuth();
  const { teams } = useTeam();

  const uid = firebaseUser?.uid || null;
  const isParent = String(userProfile?.role || '').toLowerCase() === 'parent';

  // Set of team IDs where the user is in `parentIds`. We always
  // run the query (even for users whose global role is 'parent')
  // so the team picker on the home page has the full list. Skipped
  // only when there's no auth.
  const [parentTeamIds, setParentTeamIds] = useState(() => new Set());
  useEffect(() => {
    if (!uid) {
      setParentTeamIds(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'teams'), where('parentIds', 'array-contains', uid))
        );
        if (!cancelled) {
          setParentTeamIds(new Set(snap.docs.map((d) => d.id)));
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[RoleContext] parent-teams query failed', err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  // ── Per-team role helpers ────────────────────────────────────────
  const isCoachOnTeam = useCallback(
    (teamId) => {
      if (!teamId) return false;
      // teams from TeamContext is queried by ownerId / coachUid /
      // coachId / memberUids / members / managerUids — i.e., any
      // coach-side membership. parentIds isn't in that query.
      return teams.some((t) => t.id === teamId);
    },
    [teams]
  );
  const isParentOnTeam = useCallback(
    (teamId) => {
      if (!teamId) return false;
      return parentTeamIds.has(teamId);
    },
    [parentTeamIds]
  );
  const teamRole = useCallback(
    (teamId) => {
      if (!teamId) return null;
      // Coach role wins on conflict — a coach who's also a parent of
      // their own kid on the same team gets the more capable view.
      if (isCoachOnTeam(teamId)) return 'coach';
      if (isParentOnTeam(teamId)) return 'parent';
      return null;
    },
    [isCoachOnTeam, isParentOnTeam]
  );

  const value = useMemo(
    () => ({
      isParent,
      hasParentTeams: parentTeamIds.size > 0,
      hasCoachTeams: (teams || []).length > 0,
      parentTeamIds,
      isCoachOnTeam,
      isParentOnTeam,
      teamRole,
    }),
    [isParent, parentTeamIds, teams, isCoachOnTeam, isParentOnTeam, teamRole]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    throw new Error('useRole must be used inside <RoleProvider>');
  }
  return ctx;
}
